import {
  Classes,
  FormGroup,
  FormGroupFeedback,
  IFormGroupProps,
  Intent,
  ITextAreaProps,
  Menu,
  MenuItem,
  Tag,
  UserAvatar,
} from '@pinpointhq/thumbtack';
import * as classNames from 'classnames';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField, useFormikContext } from 'formik';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BaseEditor, createEditor, Descendant, Editor, Range, Transforms } from 'slate';
import { HistoryEditor, withHistory } from 'slate-history';
import { Editable, ReactEditor, RenderElementProps, Slate, useFocused, useSelected, withReact } from 'slate-react';
import { User } from '../../../../../javascript/models';

type CustomElement = { type: 'paragraph' | 'mention'; children: CustomText[] };
type CustomText = { text: string; bold?: true };

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

interface IRichTextEditorProps {
  name: string;
  users: User[];
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  textareaProps?: Omit<ITextAreaProps, 'intent' | 'fill'>;
}

export default function RichTextEditor<T extends Descendant[]>({
  name,
  users,
  formGroupProps,
  textareaProps,
}: IRichTextEditorProps) {
  // any used as a type for FieldHelperProps as setError type won't accept string
  const [field, meta, helpers]: [FieldInputProps<T>, FieldMetaProps<T>, FieldHelperProps<T>] = useField<T>(name);
  const hasError = meta.touched && meta.error;
  const mentionMenuWrapperRef = React.useRef<HTMLDivElement>();
  const { value } = field;

  const [mentionTargetRange, setMentionTargetRange] = React.useState<Range>(null);
  const [mentionActiveIndex, setMentionActiveIndex] = React.useState(0);
  const [mentionSearch, setMentionSearch] = React.useState('');

  const renderElement = React.useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const editor = React.useMemo(() => withMentions(withReact(withHistory(createEditor()))), []);

  React.useEffect(() => {
    // @NOTE (Leo Melo) See discussion in https://github.com/ianstormtaylor/slate/issues/4767 for more details.
    // This is a workaround to the fact that Slate value is only used when the component is first mounted.
    // To reset the editor we must manually remove all nodes (to ensure no zombie nodes are left behind) and then
    // insert a new "empty" value in the form.
    if (
      value.length === 1 &&
      (value[0] as CustomElement).children.length === 1 &&
      (value[0] as CustomElement).children[0].text === ''
    ) {
      const children = [...editor.children];
      children.forEach((node) => editor.apply({ type: 'remove_node', path: [0], node }));
      editor.children = value;
    }
  }, [value]);

  const filteredUsers = users
    .filter((user) => user.fullName.toLowerCase().startsWith(mentionSearch.toLowerCase()))
    .slice(0, 10);

  const Portal = ({ children }) => {
    return ReactDOM.createPortal(children, document.body);
  };

  const selectMentionedUser = (index: number) => {
    Transforms.select(editor, mentionTargetRange);
    insertMention(editor, filteredUsers[index]);
    setMentionTargetRange(null);
  };

  const moveActiveIndex = (moveAmount: number) => {
    setMentionActiveIndex((index) => {
      const { length } = filteredUsers;
      // uses the remainder operator to flow through the list nicely
      return (length + index + moveAmount) % length;
    });
  };

  const { submitForm } = useFormikContext();

  const onKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      // This allows the users to submit the form by hitting cmd + enter or ctrl + enter if they are a windows user
      //
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        submitForm();
      }
      if (!mentionTargetRange) return null;

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          moveActiveIndex(-1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          moveActiveIndex(1);
          break;
        case 'Tab':
        case 'Enter':
          event.preventDefault();
          selectMentionedUser(mentionActiveIndex);
          break;
        case 'Escape':
          event.preventDefault();
          setMentionTargetRange(null);
          break;
      }
    },
    [mentionActiveIndex, mentionSearch, mentionTargetRange],
  );

  const menuItems = filteredUsers.map((user, index) => {
    const handleClick = () => selectMentionedUser(index);

    return (
      <MenuItem
        key={user.id}
        text={user.fullName}
        active={index === mentionActiveIndex}
        onClick={handleClick}
        icon={<UserAvatar user={user} small={true} />}
      />
    );
  });

  function onSlateChange(newValue: T) {
    helpers.setTouched(true);
    helpers.setValue(newValue);
    handleInsert();
  }

  function handleInsert() {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);
      const wordBefore = Editor.before(editor, start, { unit: 'word' });
      const before = wordBefore && Editor.before(editor, wordBefore);
      const beforeRange = before && Editor.range(editor, before, start);
      const beforeText = beforeRange && Editor.string(editor, beforeRange);
      const beforeMatch = beforeText && beforeText.match(/^@(\S+)$/);
      const after = Editor.after(editor, start);
      const afterRange = Editor.range(editor, start, after);
      const afterText = Editor.string(editor, afterRange);
      const afterMatch = afterText.match(/^(\s|$)/);

      if (beforeMatch && afterMatch) {
        setMentionTargetRange(beforeRange);
        setMentionSearch(beforeMatch[1]);
        setMentionActiveIndex(0);
        return;
      }
    }

    setMentionTargetRange(null);
  }

  React.useEffect(() => {
    if (mentionTargetRange && filteredUsers.length > 0) {
      const el = mentionMenuWrapperRef.current;
      const domRange = ReactEditor.toDOMRange(editor, mentionTargetRange);
      const rect = domRange.getBoundingClientRect();
      el.style.top = `${rect.top + window.pageYOffset + 24}px`;
      el.style.left = `${rect.left + window.pageXOffset}px`;
    }
  }, [filteredUsers.length, editor, mentionActiveIndex, mentionSearch, mentionTargetRange]);

  const classes = classNames(Classes.INPUT, { [Classes.intentClass(Intent.DANGER)]: hasError });

  /**
   * NOTE: (Tom Luce) We need this silly useEffect here as a workaround, similar to the one suggested here:
   * https://github.com/ianstormtaylor/slate/issues/4314#issuecomment-876053022
   */
  React.useEffect(() => {
    document.querySelectorAll<HTMLDivElement>('[data-slate-editor="true"]').forEach((editorEl) => {
      editorEl.style.minHeight = `80px`;
    });
  }, [value]);

  return (
    <FormGroup {...formGroupProps}>
      <Slate editor={editor} value={value} onChange={onSlateChange}>
        <Editable
          renderElement={renderElement}
          onKeyDown={onKeyDown}
          {...textareaProps}
          className={classes}
          style={{
            backgroundColor: '#fff',
            height: 'auto',
            marginBottom: '8px',
            // Currently does nothing as a Slate bug overrules this and puts it at 24px. See workaround above.
            minHeight: '80px',
          }}
        />
        {mentionTargetRange && filteredUsers.length > 0 && (
          <Portal>
            <div
              aria-label="User Mention Menu"
              ref={mentionMenuWrapperRef}
              style={{
                background: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,.2)',
                left: '-9999px',
                padding: '3px',
                position: 'absolute',
                top: '-9999px',
                zIndex: 9999,
              }}
            >
              <Menu>{menuItems}</Menu>
            </div>
          </Portal>
        )}
      </Slate>
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}

const withMentions = (editor: Editor) => {
  const { isInline, isVoid } = editor;

  editor.isInline = (element) => {
    return element.type === 'mention' ? true : isInline(element);
  };

  editor.isVoid = (element) => {
    return element.type === 'mention' ? true : isVoid(element);
  };

  return editor;
};

const insertMention = (editor: Editor, user: User) => {
  const mention = { type: 'mention' as const, user: user.fullName, userId: user.id, children: [{ text: '' }] };
  Transforms.insertNodes(editor, mention);
  Transforms.move(editor);
};

function Element(props) {
  const { attributes, children, element } = props;
  switch (element.type) {
    case 'mention':
      return <MentionElement {...props} />;
    default:
      return <p {...attributes}>{children}</p>;
  }
}

// NOTE: (TomLuce) DO NOT REMOVE { userSelect: 'none' } from the style tag
// @see https://github.com/ianstormtaylor/slate/issues/3723#issuecomment-644619153
function MentionElement({ attributes, children, element }) {
  const selected = useSelected();
  const focused = useFocused();
  return (
    <span
      {...attributes}
      contentEditable={false}
      style={{
        borderRadius: '1em',
        boxShadow: selected && focused ? '0 0 0 2px #2a3437' : 'none',
        display: 'inline-block',
        marginRight: '3px',
        padding: '1px',
        userSelect: 'none',
      }}
    >
      <Tag minimal={true}>{element.user}</Tag>
      {children}
    </span>
  );
}
